import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  House, Users, BookOpen, Cross, ChartLineUp, Crown,
  CheckCircle, Circle, ArrowRight, Play, CaretDown, CaretUp,
  Sparkle, Target, Handshake, Lightning, Heart, Trophy,
  BookBookmark, VideoCamera, Link as LinkIcon, Briefcase,
  Coins, Brain, Wallet, Fire, Star, Compass, ArrowLeft
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GettingStartedPage = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [expandedStep, setExpandedStep] = useState(1);
  const [loading, setLoading] = useState(true);

  // Fetch user's onboarding progress
  useEffect(() => {
    const fetchProgress = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/onboarding/progress`, {
          headers: getAuthHeader()
        });
        setCompletedSteps(response.data.completed_steps || []);
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, [user, getAuthHeader]);

  // Mark step as complete
  const markStepComplete = async (stepId) => {
    if (completedSteps.includes(stepId)) return;
    
    try {
      await axios.post(`${API}/onboarding/complete-step`, 
        { step_id: stepId },
        { headers: getAuthHeader() }
      );
      setCompletedSteps([...completedSteps, stepId]);
      toast.success('Step completed! Keep going, King!');
      
      // Auto-expand next step
      const currentIndex = onboardingSteps.findIndex(s => s.id === stepId);
      if (currentIndex < onboardingSteps.length - 1) {
        setExpandedStep(onboardingSteps[currentIndex + 1].id);
      }
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const isAdmin = user?.role === 'admin';
  const hasInnerCircleAccess = user?.role === 'paid_member' || isAdmin;

  const onboardingSteps = [
    {
      id: 1,
      title: 'Welcome to the Brotherhood',
      subtitle: 'Understand the POWR Mission',
      icon: Heart,
      color: '#C4A661',
      duration: '2 min read',
      content: {
        intro: "Welcome to POWR — Purpose. Ownership. Wealth. Responsibility. This platform was built specifically for Black men of faith who are ready to build wealth, strengthen their walk with God, and connect with like-minded brothers.",
        sections: [
          {
            title: "What POWR Stands For",
            items: [
              { label: "Purpose", desc: "Discover and walk in your God-given calling" },
              { label: "Ownership", desc: "Take full responsibility for your life, finances, and legacy" },
              { label: "Wealth", desc: "Build generational wealth through biblical principles" },
              { label: "Responsibility", desc: "Lead your family, community, and sphere of influence with integrity" }
            ]
          },
          {
            title: "What You'll Find Here",
            items: [
              { label: "Community", desc: "A brotherhood of men supporting each other's growth" },
              { label: "Training", desc: "Courses on wealth building, business, and faith" },
              { label: "Accountability", desc: "Partners who will challenge you to level up" },
              { label: "Resources", desc: "Tools, templates, and guides for your journey" }
            ]
          }
        ],
        scripture: {
          text: "Iron sharpens iron, and one man sharpens another.",
          reference: "Proverbs 27:17"
        },
        action: "Take a moment to set your intention. Why are you here? What do you want to achieve? Write it down somewhere — you'll need this clarity for the journey ahead."
      }
    },
    {
      id: 2,
      title: 'Navigate the Community Dashboard',
      subtitle: 'Your Home Base',
      icon: House,
      color: '#10B981',
      duration: '3 min',
      content: {
        intro: "The Community Dashboard is where you'll spend most of your time. It's designed to keep you connected, informed, and focused on your goals.",
        sections: [
          {
            title: "Left Sidebar — Navigation",
            items: [
              { label: "Feed", desc: "See posts from the entire community, share wins, ask questions" },
              { label: "Wins", desc: "Celebrate victories — big or small. Your win inspires others" },
              { label: "Accountability", desc: "Check in with your goals and see how brothers are progressing" },
              { label: "Faith", desc: "Share testimonies, prayer requests, and spiritual insights" },
              { label: "Business", desc: "Discuss strategies, opportunities, and business challenges" }
            ]
          },
          {
            title: "Right Sidebar — Quick Links",
            items: [
              { label: "Start Here", desc: "You're here now — the onboarding guide" },
              { label: "Training", desc: "Access all courses and learning materials" },
              { label: "Ecosystem Tools", desc: "Premium tools for Inner Circle members" }
            ]
          },
          {
            title: "Top Navigation",
            items: [
              { label: "POWR Logo", desc: "Click to return to the community home" },
              { label: "Admin", desc: "Access admin dashboard (admins only)" },
              { label: "Inner Circle", desc: "Premium membership area" }
            ]
          }
        ],
        action: "Go to the Feed and introduce yourself. Tell the brotherhood who you are, what you do, and what you're working toward. Don't be shy — we're all here to support each other.",
        actionLink: "/community",
        actionLabel: "Go to Community Feed"
      }
    },
    {
      id: 3,
      title: 'Start Your Day with Devotionals',
      subtitle: 'Faith First, Always',
      icon: Cross,
      color: '#8B5CF6',
      duration: '5 min daily',
      content: {
        intro: "Every day, a new devotional is waiting for you. These aren't generic devotionals — they're specifically written for Black men navigating faith and business.",
        sections: [
          {
            title: "What's Included",
            items: [
              { label: "Daily Scripture", desc: "A verse to meditate on throughout your day" },
              { label: "Reflection", desc: "Practical application for your life and business" },
              { label: "Audio Narration", desc: "Listen while you commute or work out" },
              { label: "Action Step", desc: "One concrete thing to implement today" }
            ]
          },
          {
            title: "How to Use Devotionals",
            items: [
              { label: "Morning Routine", desc: "Start each day with the devotional before checking emails" },
              { label: "Journal", desc: "Write down insights and how they apply to your situation" },
              { label: "Share", desc: "Post in the Faith section what spoke to you" },
              { label: "Apply", desc: "Actually do the action step — don't just read it" }
            ]
          }
        ],
        scripture: {
          text: "Your word is a lamp to my feet and a light to my path.",
          reference: "Psalm 119:105"
        },
        action: "Read today's devotional and listen to the audio. Then share one takeaway in the Faith section of the community.",
        actionLink: "/devotionals",
        actionLabel: "Read Today's Devotional"
      }
    },
    {
      id: 4,
      title: 'Explore the Training Library',
      subtitle: 'Level Up Your Skills',
      icon: BookOpen,
      color: '#F59E0B',
      duration: '5 min overview',
      content: {
        intro: "The Training section contains courses designed to help you build wealth, grow your business, and strengthen your faith — all from a biblical perspective.",
        sections: [
          {
            title: "Available Courses",
            items: [
              { label: "POWR Foundations", desc: "Core principles of purpose-driven wealth building" },
              { label: "AI Income Blueprint", desc: "How to leverage AI to create income streams" },
              { label: "AffiliateAI", desc: "Build passive income through strategic partnerships" },
              { label: "Business Fundamentals", desc: "Start and grow a profitable business" }
            ]
          },
          {
            title: "How to Learn Effectively",
            items: [
              { label: "Watch Actively", desc: "Take notes, pause, and reflect — don't just binge" },
              { label: "Implement Immediately", desc: "Apply one thing from each lesson before moving on" },
              { label: "Ask Questions", desc: "Post in the Business section if you're stuck" },
              { label: "Track Progress", desc: "Complete each module before moving to the next" }
            ]
          }
        ],
        action: "Browse the training library and start with the first module of any course that interests you. Commit to completing at least one lesson this week.",
        actionLink: "/courses",
        actionLabel: "Browse Training Courses"
      }
    },
    {
      id: 5,
      title: 'Connect with the Community',
      subtitle: 'Brotherhood is Everything',
      icon: Users,
      color: '#EC4899',
      duration: '10 min',
      content: {
        intro: "This platform is powerful because of the people on it. The more you engage, the more value you'll get. Don't be a lurker — participate!",
        sections: [
          {
            title: "Ways to Engage",
            items: [
              { label: "Post Regularly", desc: "Share updates, questions, wins, and challenges at least weekly" },
              { label: "Comment & Encourage", desc: "When a brother posts a win, celebrate with him" },
              { label: "Share Resources", desc: "Found something helpful? Share it with the community" },
              { label: "Ask for Help", desc: "Stuck on something? The brotherhood is here to help" }
            ]
          },
          {
            title: "Community Guidelines",
            items: [
              { label: "Be Authentic", desc: "Share your real journey — struggles and all" },
              { label: "Stay Positive", desc: "Build up, don't tear down. No negativity" },
              { label: "Keep it Clean", desc: "Language and content should honor God" },
              { label: "Give Generously", desc: "Share knowledge, connections, and encouragement freely" }
            ]
          }
        ],
        scripture: {
          text: "And let us consider how to stir up one another to love and good works.",
          reference: "Hebrews 10:24"
        },
        action: "Find 3 posts in the community and leave meaningful comments. Encourage a brother, answer a question, or share your perspective.",
        actionLink: "/community",
        actionLabel: "Engage with the Community"
      }
    },
    {
      id: 6,
      title: 'Access Resources & Links',
      subtitle: 'Tools for the Journey',
      icon: LinkIcon,
      color: '#06B6D4',
      duration: '3 min',
      content: {
        intro: "The Resources section contains curated tools, templates, and external links to help you on your journey. These are hand-picked resources that we trust and use ourselves.",
        sections: [
          {
            title: "What You'll Find",
            items: [
              { label: "Business Templates", desc: "Contracts, proposals, financial trackers" },
              { label: "Recommended Books", desc: "Must-read books for faith and business" },
              { label: "Useful Tools", desc: "Software and apps we recommend" },
              { label: "External Links", desc: "Vetted resources from across the web" }
            ]
          },
          {
            title: "Resource Hub Features",
            items: [
              { label: "Videos", desc: "Curated video content for learning" },
              { label: "Links", desc: "Quick access to important external resources" },
              { label: "Downloads", desc: "Templates and guides you can save" }
            ]
          }
        ],
        action: "Explore the Resources section and bookmark at least one resource that will help you this week.",
        actionLink: "/resources",
        actionLabel: "Browse Resources"
      }
    },
    {
      id: 7,
      title: 'Discover Investment Tools',
      subtitle: 'Build Wealth Intentionally',
      icon: ChartLineUp,
      color: '#10B981',
      duration: '5 min',
      locked: !hasInnerCircleAccess,
      content: {
        intro: "The Investment Tools section helps you build wealth through practical, hands-on features. These tools turn knowledge into action.",
        sections: [
          {
            title: "Available Tools",
            items: [
              { label: "$5 Movement", desc: "Commit to investing just $5/week and track your progress. Small steps, big results." },
              { label: "Paper Portfolio", desc: "Practice stock trading with virtual money before risking real capital." },
              { label: "Wealth Intelligence", desc: "AI-powered insights and education about investing and wealth building." }
            ]
          },
          {
            title: "Why These Tools Matter",
            items: [
              { label: "Build Habits", desc: "Consistent small actions create lasting change" },
              { label: "Learn Risk-Free", desc: "Make mistakes with fake money, not real money" },
              { label: "Stay Informed", desc: "Knowledge is power when it comes to investing" }
            ]
          }
        ],
        scripture: {
          text: "The plans of the diligent lead surely to abundance.",
          reference: "Proverbs 21:5"
        },
        action: "Start the $5 Movement — make your first pledge and commit to building wealth one week at a time.",
        actionLink: "/5-movement-platform",
        actionLabel: "Join $5 Movement"
      }
    },
    {
      id: 8,
      title: 'Explore Inner Circle Features',
      subtitle: 'Premium Brotherhood Experience',
      icon: Crown,
      color: '#C4A661',
      duration: '5 min',
      locked: !hasInnerCircleAccess,
      content: {
        intro: hasInnerCircleAccess 
          ? "As an Inner Circle member, you have access to exclusive features designed to accelerate your growth. Here's how to maximize your membership."
          : "The Inner Circle is our premium membership tier with exclusive features. Here's what's available when you upgrade.",
        sections: [
          {
            title: "Inner Circle Features",
            items: [
              { label: "Mastermind", desc: "Connect with high-level brothers for strategic discussions" },
              { label: "Prayer Wall", desc: "Private space for deep prayer requests and intercession" },
              { label: "IC Dashboard", desc: "Track your Inner Circle progress and engagement" },
              { label: "Business Suite", desc: "Premium business tools and resources" }
            ]
          },
          {
            title: "How to Maximize Membership",
            items: [
              { label: "Show Up Consistently", desc: "Regular participation compounds results" },
              { label: "Be Vulnerable", desc: "The deeper you go, the more you grow" },
              { label: "Give Back", desc: "Share your wins and lessons with newer members" },
              { label: "Take Action", desc: "Information without implementation is useless" }
            ]
          }
        ],
        action: hasInnerCircleAccess 
          ? "Explore the Mastermind section and introduce yourself to the Inner Circle community."
          : "Consider upgrading to Inner Circle to unlock these powerful features.",
        actionLink: hasInnerCircleAccess ? "/inner-circle-hub" : "/inner-circle-sales",
        actionLabel: hasInnerCircleAccess ? "Go to Inner Circle" : "Learn About Inner Circle"
      }
    },
    {
      id: 9,
      title: 'Set Your Goals & Take Action',
      subtitle: 'Your Journey Starts Now',
      icon: Target,
      color: '#EF4444',
      duration: '10 min',
      content: {
        intro: "You've learned about the platform. Now it's time to set clear goals and start taking action. Vision without execution is just a dream.",
        sections: [
          {
            title: "Goal Setting Framework",
            items: [
              { label: "90-Day Vision", desc: "Where do you want to be in 90 days? Be specific." },
              { label: "Monthly Milestones", desc: "Break your 90-day goal into 3 monthly checkpoints" },
              { label: "Weekly Actions", desc: "What will you do THIS WEEK to move forward?" },
              { label: "Daily Habits", desc: "What daily disciplines will you commit to?" }
            ]
          },
          {
            title: "Recommended First Week Actions",
            items: [
              { label: "Day 1", desc: "Introduce yourself in the Feed, read today's devotional" },
              { label: "Day 2", desc: "Start one training course, complete first lesson" },
              { label: "Day 3", desc: "Comment on 5 posts, encourage brothers" },
              { label: "Day 4", desc: "Share a win or challenge in the community" },
              { label: "Day 5", desc: "Explore Investment Tools, make a pledge" },
              { label: "Day 6", desc: "Review resources, download one template" },
              { label: "Day 7", desc: "Rest, reflect, and plan your next week" }
            ]
          }
        ],
        scripture: {
          text: "Commit your work to the Lord, and your plans will be established.",
          reference: "Proverbs 16:3"
        },
        action: "Write down your 90-day goal and share it in the Accountability section. Make it public — accountability accelerates achievement.",
        actionLink: "/community",
        actionLabel: "Share Your Goal"
      }
    }
  ];

  const progressPercentage = Math.round((completedSteps.length / onboardingSteps.length) * 100);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center p-8">
          <Compass size={64} className="mx-auto mb-6" style={{ color: '#C4A661' }} />
          <h1 className="text-2xl font-bold mb-4" style={{ color: '#fff' }}>Sign In to Get Started</h1>
          <p className="mb-6" style={{ color: '#888' }}>Access the onboarding guide by signing in to your account.</p>
          <Link to="/login" className="inline-block px-6 py-3 rounded-lg font-semibold" style={{ backgroundColor: '#C4A661', color: '#000' }}>
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 px-6 py-4" style={{ backgroundColor: '#111', borderBottom: '1px solid #222' }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/community" className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity" style={{ color: '#C4A661' }}>
            <ArrowLeft size={18} />
            Back to Community
          </Link>
          <div className="flex items-center gap-3">
            <Compass size={24} style={{ color: '#C4A661' }} />
            <span className="text-lg font-bold" style={{ color: '#fff' }}>Getting Started</span>
          </div>
          <div className="text-sm" style={{ color: '#888' }}>
            {completedSteps.length}/{onboardingSteps.length} Complete
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="px-6 py-4" style={{ backgroundColor: '#111' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: '#fff' }}>Your Progress</span>
            <span className="text-sm font-bold" style={{ color: '#C4A661' }}>{progressPercentage}%</span>
          </div>
          <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#222' }}>
            <motion.div 
              className="h-full rounded-full"
              style={{ backgroundColor: '#C4A661' }}
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Welcome Message */}
        {completedSteps.length === 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-xl text-center"
            style={{ backgroundColor: 'rgba(201, 166, 97, 0.1)', border: '1px solid rgba(201, 166, 97, 0.3)' }}
          >
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#C4A661' }}>Welcome, {user?.name?.split(' ')[0] || 'Brother'}!</h2>
            <p style={{ color: '#ccc' }}>Complete each step below to get the most out of POWR. Take your time — this journey is worth doing right.</p>
          </motion.div>
        )}

        {/* Completion Message */}
        {completedSteps.length === onboardingSteps.length && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-8 rounded-xl text-center"
            style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}
          >
            <Trophy size={64} className="mx-auto mb-4" style={{ color: '#10B981' }} />
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#10B981' }}>Onboarding Complete!</h2>
            <p className="mb-4" style={{ color: '#ccc' }}>You're all set up and ready to thrive. Now the real work begins!</p>
            <Link to="/community" className="inline-block px-6 py-3 rounded-lg font-semibold" style={{ backgroundColor: '#10B981', color: '#fff' }}>
              Go to Community
            </Link>
          </motion.div>
        )}

        {/* Steps */}
        <div className="space-y-4">
          {onboardingSteps.map((step, index) => {
            const isCompleted = completedSteps.includes(step.id);
            const isExpanded = expandedStep === step.id;
            const isLocked = step.locked;
            
            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="rounded-xl overflow-hidden"
                style={{ 
                  backgroundColor: '#111', 
                  border: `1px solid ${isCompleted ? '#10B981' : isLocked ? '#333' : '#222'}`,
                  opacity: isLocked ? 0.6 : 1
                }}
              >
                {/* Step Header */}
                <button
                  onClick={() => !isLocked && setExpandedStep(isExpanded ? null : step.id)}
                  className="w-full px-6 py-4 flex items-center gap-4 text-left"
                  disabled={isLocked}
                >
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: isCompleted ? 'rgba(16, 185, 129, 0.2)' : `${step.color}20` }}
                  >
                    {isCompleted ? (
                      <CheckCircle size={24} weight="fill" style={{ color: '#10B981' }} />
                    ) : isLocked ? (
                      <Crown size={24} style={{ color: '#C4A661' }} />
                    ) : (
                      <step.icon size={24} style={{ color: step.color }} />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: step.color }}>
                        Step {step.id}
                      </span>
                      {isLocked && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(201, 166, 97, 0.2)', color: '#C4A661' }}>
                          Inner Circle
                        </span>
                      )}
                      {isCompleted && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981' }}>
                          Completed
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold truncate" style={{ color: '#fff' }}>{step.title}</h3>
                    <p className="text-sm truncate" style={{ color: '#888' }}>{step.subtitle} · {step.duration}</p>
                  </div>
                  
                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <CaretUp size={24} style={{ color: '#888' }} />
                    ) : (
                      <CaretDown size={24} style={{ color: '#888' }} />
                    )}
                  </div>
                </button>

                {/* Step Content */}
                <AnimatePresence>
                  {isExpanded && !isLocked && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-2 border-t" style={{ borderColor: '#222' }}>
                        {/* Intro */}
                        <p className="mb-6 leading-relaxed" style={{ color: '#ccc' }}>
                          {step.content.intro}
                        </p>

                        {/* Sections */}
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                          {step.content.sections.map((section, sIdx) => (
                            <div key={sIdx} className="p-4 rounded-lg" style={{ backgroundColor: '#0a0a0a' }}>
                              <h4 className="font-bold mb-3" style={{ color: step.color }}>{section.title}</h4>
                              <ul className="space-y-2">
                                {section.items.map((item, iIdx) => (
                                  <li key={iIdx} className="flex items-start gap-2">
                                    <CheckCircle size={16} className="flex-shrink-0 mt-1" style={{ color: step.color }} />
                                    <div>
                                      <span className="font-semibold" style={{ color: '#fff' }}>{item.label}: </span>
                                      <span style={{ color: '#aaa' }}>{item.desc}</span>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>

                        {/* Scripture */}
                        {step.content.scripture && (
                          <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
                            <p className="italic mb-2" style={{ color: '#fff' }}>"{step.content.scripture.text}"</p>
                            <p className="text-sm" style={{ color: '#8B5CF6' }}>— {step.content.scripture.reference}</p>
                          </div>
                        )}

                        {/* Action */}
                        <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: 'rgba(201, 166, 97, 0.1)', border: '1px solid rgba(201, 166, 97, 0.3)' }}>
                          <div className="flex items-center gap-2 mb-2">
                            <Lightning size={20} style={{ color: '#C4A661' }} />
                            <span className="font-bold" style={{ color: '#C4A661' }}>Action Step</span>
                          </div>
                          <p style={{ color: '#ccc' }}>{step.content.action}</p>
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-wrap items-center gap-4">
                          {step.content.actionLink && (
                            <Link
                              to={step.content.actionLink}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all hover:scale-105"
                              style={{ backgroundColor: step.color, color: step.color === '#C4A661' ? '#000' : '#fff' }}
                            >
                              {step.content.actionLabel}
                              <ArrowRight size={18} />
                            </Link>
                          )}
                          
                          {!isCompleted && (
                            <button
                              onClick={() => markStepComplete(step.id)}
                              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all hover:scale-105"
                              style={{ backgroundColor: '#222', color: '#fff', border: '1px solid #333' }}
                            >
                              <CheckCircle size={18} />
                              Mark as Complete
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Need Help */}
        <div className="mt-12 p-6 rounded-xl text-center" style={{ backgroundColor: '#111', border: '1px solid #222' }}>
          <Handshake size={48} className="mx-auto mb-4" style={{ color: '#C4A661' }} />
          <h3 className="text-xl font-bold mb-2" style={{ color: '#fff' }}>Need Help?</h3>
          <p className="mb-4" style={{ color: '#888' }}>
            If you have questions or need support, reach out in the community. The brotherhood is here for you.
          </p>
          <Link to="/community" className="inline-block px-6 py-3 rounded-lg font-semibold" style={{ backgroundColor: '#222', color: '#fff', border: '1px solid #333' }}>
            Ask the Community
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GettingStartedPage;
